/**
 * Contextual secondary rail for the admin Analytics dashboards. Management of
 * distributors/MSPs/companies lives in the unified Organizations page; the
 * per-level analytics dashboards (+ Credentials by MSP) keep their own routes,
 * and this rail lets you hop between them. Renders nothing outside those routes.
 *
 * The rail is driven by `useScopeHierarchy`, the same hook the scope bar and the
 * dashboards themselves read, so the counts beside each level are the number of
 * rows that level's page will actually show. That matters more than it sounds:
 * a static rail gave no indication that picking a distributor upstream had cut
 * the MSP list from thirty to three, so the numbers on the page looked wrong
 * rather than filtered. The hook caches per session, so no request is made on
 * the rail's behalf.
 *
 * The frame, rows, counts and fold are `ContextRail`'s, shared with Desk's
 * rail and the credentials page's, so the three contextual rails are one
 * object rather than three that each chose their own washes. The width key is
 * the one this rail always had, so a width already dragged is kept; the fold
 * is new, under its own key.
 */

import type { ElementType } from "react";
import { useLocation } from "react-router-dom";
import { BarChart3, Building2, Key, Layers } from "lucide-react";

import {
  ContextRail,
  RailIcon,
  RailIconGroup,
  RailLink,
} from "@/components/layout/ContextRail";
import { usePermissions } from "@/hooks/usePermissions";
import { useScopeHierarchy } from "@/hooks/useScopeHierarchy";
import { useAuthStore } from "@/store/authStore";
import { mspCredentialsCapabilities } from "@/features/admin/credentials/mspCredentialsCapabilities";

interface RailItem {
  name: string;
  href: string;
  icon: ElementType;
  show: boolean;
  /** Rows this dashboard will list. `undefined` renders no count at all. */
  count?: number;
}

const RAIL_ROUTES = [
  "/admin/organizations/distributors/analytics",
  "/admin/organizations/msps/analytics",
  "/admin/organizations/msps/credentials",
  "/admin/organizations/companies/analytics",
];

export function SectionRail() {
  const { pathname } = useLocation();
  if (
    !RAIL_ROUTES.some((r) => pathname === r || pathname.startsWith(r + "/"))
  ) {
    return null;
  }
  return <SectionRailBody />;
}

/**
 * Split from the route check so the scope hook runs only on the dashboards
 * that use it, not on every route the layout renders.
 */
function SectionRailBody() {
  const { isSystemAdmin, isDistributor, isMSPAdmin } = usePermissions();
  const role = useAuthStore((s) => s.user?.role);
  const {
    distributors,
    msps,
    companies,
    selectedDistributorId,
    isLoading,
    scopeLabel,
  } = useScopeHierarchy();

  // `distributors` is the scope *picker's* pool, which stays full for a
  // platform admin so they can still switch away — unlike `msps`/`companies`,
  // it is not narrowed by the current selection. Taking it at face value made
  // the rail read "Distributors 2" above a page listing one. The count has to
  // be what the dashboard will render, not what the picker can offer.
  const distributorCount = selectedDistributorId
    ? distributors.filter((d) => String(d.id) === String(selectedDistributorId))
        .length
    : distributors.length;

  const items: RailItem[] = [
    {
      name: "Distributors",
      href: "/admin/organizations/distributors/analytics",
      icon: Layers,
      show: isSystemAdmin,
      count: distributorCount,
    },
    {
      name: "MSPs",
      href: "/admin/organizations/msps/analytics",
      icon: Building2,
      show: isSystemAdmin || isDistributor,
      count: msps.length,
    },
    {
      name: "Companies",
      href: "/admin/organizations/companies/analytics",
      icon: BarChart3,
      show: isSystemAdmin || isDistributor || isMSPAdmin,
      count: companies.length,
    },
    {
      // Credentials are not an organization level, so a row count here would
      // be a different kind of number sitting in the same column.
      name: "Credentials by MSP",
      href: "/admin/organizations/msps/credentials",
      icon: Key,
      show: mspCredentialsCapabilities(role).canView,
    },
  ];

  const visible = items.filter((i) => i.show);
  // While the scope lists load, a row that will have a count holds its place
  // with a skeleton instead of showing a zero it cannot stand behind.
  const pending = (item: RailItem) => isLoading && item.count !== undefined;

  return (
    <ContextRail
      title="Analytics"
      label="Analytics sections"
      foldLabel="Analytics sections"
      widthKey="ec-analytics-rail-w-v1"
      foldKey="ec-analytics-rail-collapsed-v1"
      defaultWidth={208}
      minWidth={168}
      maxWidth={360}
      folded={
        <RailIconGroup>
          {visible.map((item) => (
            <RailIcon
              key={item.href}
              to={item.href}
              icon={item.icon}
              name={item.name}
              count={item.count}
              countPending={pending(item)}
            />
          ))}
        </RailIconGroup>
      }
      // States what the counts above are counting. Without it, a filtered
      // rail and an unfiltered one look identical.
      footer={
        <>
          Scoped to{" "}
          <span className="text-foreground font-medium">{scopeLabel}</span>
        </>
      }
    >
      <div className="space-y-0.5">
        {visible.map((item) => (
          <RailLink
            key={item.href}
            to={item.href}
            icon={item.icon}
            label={item.name}
            count={item.count}
            countPending={pending(item)}
          />
        ))}
      </div>
    </ContextRail>
  );
}
